import { useState, useEffect } from 'react';

import CustomModal from '@/components/CustomModal';
import { RadioButtonGroup } from '@/components/RadioButton';
import Select from '@/components/Select';
import { LESSON_STATUS_OPTIONS } from '@/constants/constant';
import { useFormatText } from '@/hooks/useFormatText';
import { getClassDropdown } from '@/service/classService';

interface IFilterDrawerProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onApplyFilters?: (filters: ILessonFilters) => void;
}

export interface ILessonFilters {
  classAssigned?: string | string[];
  // stages?: string[];
  status?: string;
}

const FilterDrawer = ({
  isOpen,
  setIsOpen,
  onApplyFilters,
}: IFilterDrawerProps) => {
  const { formatText } = useFormatText();

  // Filter state
  const [status, setStatus] = useState<string>('');
  const [classAssigned, setClassAssigned] = useState<string | string[]>([]);
  const [classOptions, setClassOptions] = useState<
    { value: string; label: string }[]
  >([]);

  // Fetch class options
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const res = await getClassDropdown();

        if (res?.data && Array.isArray(res.data)) {
          setClassOptions(
            res.data.map((c: { id: string; name: string }) => ({
              value: c.id,
              label: c.name,
            }))
          );
        }
      } catch (error) {
        console.error('Failed to fetch dropdowns', error);
      }
    };

    fetchDropdowns();
  }, []);

  // Reset filters to initial state
  const handleClearFilters = () => {
    setStatus('');
    setClassAssigned([]);
    onApplyFilters?.({
      status: '',
      classAssigned: [],
      // stages: [],
    });
    setIsOpen(false);
  };

  // Apply filters and close drawer
  const handleApplyFilters = () => {
    onApplyFilters?.({
      classAssigned,
      // stages,
      status,
    });
    setIsOpen(false);
  };

  // Reset filters when drawer is closed without applying
  useEffect(() => {
    if (!isOpen) {
      // Optionally reset to default or keep current state
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      position="right"
      isHeader
      heading={formatText('lessons.filter-drawer.title')}
      isFooter
      cancelButtonProps={{
        variant: 'link',
        label: formatText('lessons.filter-drawer.clear-filters'),
        onClick: handleClearFilters,
      }}
      confirmButtonProps={{
        label: formatText('lessons.filter-drawer.show-results'),
        onClick: handleApplyFilters,
        variant: 'primary',
        className: 'px-6',
      }}
    >
      {/* Filter Form */}
      <div
        className="w-full flex flex-col gap-6"
        data-testid="filter-drawer-form"
      >
        {/* Status Filter */}
        <div data-testid="status-filter-container">
          <RadioButtonGroup
            name="status"
            label={formatText('lessons.filter-drawer.status-label')}
            options={LESSON_STATUS_OPTIONS}
            value={status}
            onChange={setStatus}
            direction="row"
            gap="gap-8"
            dataTestId="status-filter"
            formatLabel={formatText}
          />
        </div>

        {/* Class Assigned Filter */}
        <div data-testid="class-assigned-filter-container">
          <label className="block text-sm font-medium text-neutral-primary mb-2">
            {formatText('lessons.filter-drawer.class-assigned-label')}
          </label>
          <Select
            options={classOptions}
            selected={classAssigned}
            isSearch
            onChange={setClassAssigned}
            placeholder={formatText(
              'lessons.filter-drawer.class-assigned-placeholder'
            )}
            className="w-full"
            isMulti
            isClearable
            dataTestId="class-assigned-filter-select"
          />
        </div>

        {/* Level Filter */}
        {/* <div data-testid="level-filter-container">
          <label className="block text-sm font-medium text-neutral-primary mb-2">
            {formatText('lessons.filter-drawer.levels-label')}
          </label>
          <Select
            options={LESSON_STAGE_OPTIONS_MENU}
            selected={stages}
            onChange={(value) =>
              setStages(Array.isArray(value) ? value : [value])
            }
            placeholder={formatText('lessons.filter-drawer.levels-placeholder')}
            className="w-full"
            isMulti
            isTextFormat
            isClearable
            dataTestId="level-filter-select"
          />
        </div> */}
      </div>
    </CustomModal>
  );
};

export default FilterDrawer;
